// Flow Builder export utilities — snapshot the ReactFlow canvas to PNG/PDF.
// Supports two modes:
//   • 'viewport' — export exactly what's currently visible
//   • 'full'     — fit all nodes into a large offscreen canvas, then export
//
// Uses html-to-image (works well with ReactFlow) + jsPDF for PDF.

import { toPng } from 'html-to-image';
import { jsPDF } from 'jspdf';

const CANVAS_SELECTOR = '.react-flow';
const VIEWPORT_SELECTOR = '.react-flow__viewport';
const NODES_SELECTOR = '.react-flow__node';

const BACKGROUND = '#f8fafc'; // slate-50 to match the canvas frame

/** Compute the bounding box of all rendered nodes on the canvas. */
function computeNodesBounds(container) {
  const nodes = container.querySelectorAll(NODES_SELECTOR);
  if (nodes.length === 0) return null;

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  nodes.forEach((n) => {
    // Nodes are absolutely positioned inside .react-flow__viewport; read the
    // computed style (translate + width/height) so we get true canvas coords.
    const style = window.getComputedStyle(n);
    const transform = new DOMMatrixReadOnly(style.transform === 'none' ? 'matrix(1,0,0,1,0,0)' : style.transform);
    const x = transform.e;
    const y = transform.f;
    const w = n.offsetWidth;
    const h = n.offsetHeight;
    if (x < minX) minX = x;
    if (y < minY) minY = y;
    if (x + w > maxX) maxX = x + w;
    if (y + h > maxY) maxY = y + h;
  });

  return { minX, minY, maxX, maxY, width: maxX - minX, height: maxY - minY };
}

/** Snapshot the entire ReactFlow container (current viewport view). */
async function snapshotViewport(container) {
  return toPng(container, {
    backgroundColor: BACKGROUND,
    pixelRatio: 2,
    cacheBust: true,
    skipFonts: true,
    filter: (node) => {
      // Strip UI chrome that shouldn't appear in exports
      if (node.classList?.contains('react-flow__minimap')) return false;
      if (node.classList?.contains('react-flow__controls')) return false;
      if (node.classList?.contains('react-flow__attribution')) return false;
      return true;
    },
  });
}

/** Snapshot the full canvas by temporarily transforming the viewport so every
 *  node fits within the container bounds. Restores the original transform even
 *  when the snapshot fails, so the live UI is never left in a broken state. */
async function snapshotFullCanvas(container) {
  const viewport = container.querySelector(VIEWPORT_SELECTOR);
  if (!viewport) return snapshotViewport(container);

  const bounds = computeNodesBounds(container);
  if (!bounds || bounds.width === 0 || bounds.height === 0) {
    return snapshotViewport(container);
  }

  const containerRect = container.getBoundingClientRect();
  const padding = 40;
  const scaleX = (containerRect.width - padding * 2) / bounds.width;
  const scaleY = (containerRect.height - padding * 2) / bounds.height;
  const scale = Math.min(scaleX, scaleY, 1); // never up-scale

  const translateX = padding - bounds.minX * scale;
  const translateY = padding - bounds.minY * scale;

  const originalTransform = viewport.style.transform;
  const originalTransition = viewport.style.transition;

  try {
    viewport.style.transition = 'none';
    viewport.style.transform = `translate(${translateX}px, ${translateY}px) scale(${scale})`;
    // Let the DOM paint before snapshotting
    await new Promise((resolve) => requestAnimationFrame(() => setTimeout(resolve, 60)));
    return await snapshotViewport(container);
  } finally {
    viewport.style.transform = originalTransform;
    viewport.style.transition = originalTransition;
  }
}

/** Download a base64 image as a PNG file. */
function downloadPng(dataUrl, filename) {
  const link = document.createElement('a');
  link.download = filename;
  link.href = dataUrl;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/** Wrap the PNG in a landscape PDF page sized to the image dimensions. */
async function downloadPdf(dataUrl, filename) {
  // Get intrinsic pixel dimensions to size the PDF page correctly
  const img = new Image();
  img.src = dataUrl;
  await new Promise((resolve) => {
    if (img.complete) resolve();
    else img.onload = resolve;
  });

  const orientation = img.width >= img.height ? 'landscape' : 'portrait';
  const pdf = new jsPDF({
    orientation,
    unit: 'pt',
    format: [img.width, img.height],
    compress: true,
  });
  pdf.addImage(dataUrl, 'PNG', 0, 0, img.width, img.height);
  pdf.save(filename);
}

/**
 * Public API — export the current Flow Builder canvas.
 *
 * @param {'viewport'|'full'} mode
 * @param {'png'|'pdf'} format
 * @param {string} [baseName='flow-builder']
 * @returns {Promise<void>}
 */
export async function exportFlowCanvas(mode = 'viewport', format = 'png', baseName = 'flow-builder') {
  const container = document.querySelector(CANVAS_SELECTOR);
  if (!container) {
    throw new Error('Flow Builder canvas not found');
  }

  const dataUrl = mode === 'full'
    ? await snapshotFullCanvas(container)
    : await snapshotViewport(container);

  const ts = new Date().toISOString().slice(0, 19).replace(/[T:]/g, '-');
  const suffix = mode === 'full' ? 'full' : 'view';
  const filename = `${baseName}-${suffix}-${ts}.${format}`;

  if (format === 'pdf') {
    await downloadPdf(dataUrl, filename);
  } else {
    downloadPng(dataUrl, filename);
  }
}
